import { useCallback, useState } from 'react'
import { errors, InferNative, Type } from '~/prix'
import { BaseActionDefinitionEnhanced } from '~/prix/actionDefinition'
import { AppError } from '../../error'
import useAPI from './api'
import { isDevelopment } from '~/utils/shared/platform'
import objectHash from 'object-hash'
import { cacheIsEnabled, get, set } from '~/prix/utils/browserCache'

export default function useActionFunction<
  InputType extends Type,
  OutputType extends Type,
  ActionDefinition extends BaseActionDefinitionEnhanced<InputType, OutputType>,
  Input = InferNative<InputType>,
  Output = InferNative<OutputType>,
>(
  actionDefinition: ActionDefinition,
  {
    staleWhileRevalidate = false,
    sourceKey = 'oltp',
    initialIsLoading = true,
  }: {
    staleWhileRevalidate?: boolean
    sourceKey?: string
    initialIsLoading?: boolean
  } = {},
) {
  const [result, setResult] = useState<Output>()
  const [error, setError] = useState<Error | AppError | null>(null)
  const [isLoading, setIsLoading] = useState(initialIsLoading)

  const api = useAPI()
  const source = api.sources[sourceKey]

  const callAction = useCallback(
    (input: Input) => {
      if (!source) {
        throw errors.incompatible('Fonte de dados indisponível')
      }

      setError(null)
      setIsLoading(true)

      // Cache preparation
      const now = +new Date()
      const cache =
        actionDefinition.cache && cacheIsEnabled
          ? actionDefinition.cache(input as InferNative<InputType>)
          : null
      const hash =
        cache &&
        cache.front &&
        (cache.front.expirationMsForUpdate || cache.front.expirationMsForFallback)
          ? objectHash(input as InferNative<InputType>, {
              respectType: false,
            })
          : null
      const cacheName = hash ? `${actionDefinition.key}.${hash}` : null

      const cacheResultPromise: Promise<{
        value: any
        timestamp: number
        expirationTimestamp: number
      } | null> = cacheName
        ? get(cacheName).catch((error: Error) => {
            console.error(error)
            return null
          })
        : Promise.resolve(null)

      let call: {
        run: () => Promise<any>
        abort: () => Promise<void>
      }

      const resultPromise = cacheResultPromise.then(cacheResult => {
        if (cacheResult !== null) {
          const isBestCache =
            cacheResult !== null &&
            cache!.front!.expirationMsForUpdate &&
            cacheResult.timestamp + cache!.front!.expirationMsForUpdate > now
          if (staleWhileRevalidate && !isBestCache) {
            console.log('Using stale cache while revalidating', cacheName, input)
            setResult(cacheResult.value)
            setIsLoading(false)
          } else if (isBestCache) {
            console.log('Using directly from cache', cacheName, input)
            setResult(cacheResult.value)
            setIsLoading(false)
            return cacheResult.value
          }
        }

        try {
          call = source.call(actionDefinition, input, api.context)
        } catch (error) {
          setError(error as Error)
          setIsLoading(false)
          throw error
        }
        return call
          .run()
          .then(result => {
            setResult(result as any)
            if (cacheName) {
              console.log('Saving to cache', cacheName, input, result)
              set(
                cacheName,
                result,
                Math.max(
                  cache!.front!.expirationMsForUpdate || 0,
                  cache!.front!.expirationMsForFallback || 0,
                ),
              ).catch(error => console.error(error))
            }
            return result
          })
          .catch(error => {
            if (
              cacheName &&
              cacheResult !== null &&
              cache!.front!.expirationMsForFallback &&
              cacheResult.timestamp + cache!.front!.expirationMsForFallback > now
            ) {
              console.error(error)
              console.log('Using fallback cache', cacheName, input)
              setResult(cacheResult!.value)
              return cacheResult!.value
            }

            setError(error)
            throw error
          })
          .finally(() => setIsLoading(false))
      })

      return {
        abort: () => call?.abort(),
        resultPromise,
      }
    },
    [source, api.context, actionDefinition],
  )

  return {
    result,
    error,
    isLoading,
    callAction,
  }
}
